/**
 * Nordicli valguskalkulaator.
 *
 * <nl-light-calc>   – täiskalkulaator (leht /pages/valguskalkulaator)
 * <nl-calc-product> – väike kalkulaator tootelehel („Mitu tükki vajan?“)
 *
 * Tooteandmed tulevad kollektsiooni alternatiivmallist /collections/<handle>?view=nl-calc
 * (vt templates/collection.nl-calc.liquid). Omahinda brauserisse ei saadeta.
 */

const PRICE_KWH = 0.2;
const FREE_SHIPPING_CENTS = 9000;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Soovituslik valgushulk lm/m² (kodus levinud soovitused, ärihoonetes EN 12464-1 järgi). */
export const ROOMS = {
  elutuba: { name: 'Elutuba', lm: 150, tone: 'soe', types: ['laevalgusti', 'allvalgustid', 'pinnapaneel', 'pirn', 'sein'], l: 5, w: 4, h: 2.7, hours: 1460, icon: 'sofa' },
  magamistuba: { name: 'Magamistuba', lm: 120, tone: 'soe', types: ['laevalgusti', 'allvalgustid', 'pinnapaneel', 'pirn', 'sein'], l: 4, w: 3.5, h: 2.7, hours: 730, icon: 'bed' },
  kook: { name: 'Köök', lm: 250, tone: 'neutraalne', types: ['allvalgustid', 'pinnapaneel', 'laevalgusti', 'pirn', 'lineaar'], l: 4, w: 3, h: 2.7, hours: 1460, icon: 'pot' },
  vannituba: { name: 'Vannituba', lm: 200, tone: 'neutraalne', types: ['laevalgusti', 'pinnapaneel', 'pirn'], ip: 44, l: 2.5, w: 2, h: 2.5, hours: 550, icon: 'bath' },
  esik: { name: 'Esik ja koridor', lm: 100, tone: 'soe', types: ['allvalgustid', 'laevalgusti', 'pinnapaneel', 'pirn', 'sein'], l: 4, w: 1.5, h: 2.7, hours: 1100, icon: 'door' },
  lastetuba: { name: 'Lastetuba', lm: 200, tone: 'neutraalne', types: ['laevalgusti', 'allvalgustid', 'pinnapaneel', 'pirn'], l: 3.5, w: 3, h: 2.7, hours: 1100, icon: 'star' },
  kodukontor: { name: 'Kodukontor', lm: 400, tone: 'neutraalne', types: ['laevalgusti', 'pinnapaneel', 'allvalgustid', 'pirn', 'lineaar'], l: 3, w: 3, h: 2.7, hours: 1250, icon: 'laptop' },
  kontor: { name: 'Kontor', lm: 900, lux: 500, tone: 'neutraalne', types: ['paneel', 'pinnapaneel', 'allvalgustid', 'lineaar', 'toru'], l: 8, w: 5, h: 2.8, hours: 2500, icon: 'office' },
  kauplus: { name: 'Kauplus', lm: 550, lux: 300, tone: 'neutraalne', types: ['allvalgustid', 'paneel', 'lineaar', 'toru'], l: 10, w: 6, h: 3, hours: 3720, icon: 'bag' },
  garaaz: { name: 'Garaaž ja töökoda', lm: 450, lux: 250, tone: 'kylm', types: ['karg', 'korglao', 'laevalgusti', 'pinnapaneel', 'lineaar', 'toru'], l: 6, w: 4, h: 2.8, hours: 730, work: true, icon: 'car' },
  ladu: { name: 'Ladu', lm: 300, lux: 150, tone: 'kylm', types: ['korglao', 'paneel', 'lineaar', 'toru'], l: 25, w: 20, h: 8, hours: 3120, work: true, icon: 'box' },
  oue: { name: 'Õu ja sissesõit', lm: 50, lux: 20, tone: 'neutraalne', types: ['prozektor'], l: 10, w: 8, h: 4.5, hours: 1460, outdoor: true, icon: 'tree' },
  parkla: { name: 'Parkla', lm: 40, lux: 15, tone: 'neutraalne', types: ['prozektor'], l: 20, w: 15, h: 6, hours: 3650, outdoor: true, icon: 'parking' },
  laoplats: { name: 'Laoplats ja tööala', lm: 120, lux: 50, tone: 'kylm', types: ['prozektor'], l: 20, w: 15, h: 6, hours: 3000, outdoor: true, work: true, icon: 'crane' },
  spordiplats: { name: 'Spordiplats', lm: 350, lux: 150, tone: 'kylm', types: ['prozektor'], l: 30, w: 15, h: 8, hours: 700, outdoor: true, work: true, icon: 'ball' },
};

export const TYPES = {
  laevalgusti: {
    name: 'Laevalgusti', desc: 'Üks või kaks valgustit lakke', col: 'laevalgustid-pealepaigaldatavad', unit: ['laevalgusti', 'laevalgustit'],
    exclude: /seinavalgusti|avarii|allvalgusti|tarvik/i,
    limits: (a) => ({ min: 1, max: a <= 24 ? 1 : a <= 40 ? 2 : Math.ceil(a / 20) }),
  },
  allvalgustid: {
    name: 'Allvalgustid', desc: 'Mitu valgustit ühtlaselt üle lae', col: 'suvistatavad-allvalgustid', unit: ['allvalgusti', 'allvalgustit'],
    exclude: null,
    limits: (a) => ({ min: Math.max(2, Math.ceil(a / 4)), max: Math.ceil(a) + 2 }),
  },
  pinnapaneel: {
    name: 'Pinnapaneel', desc: 'Õhuke paneel otse lakke', col: 'paneelid-pealepaigaldatavad', unit: ['paneel', 'paneeli'],
    exclude: null,
    limits: (a) => ({ min: a > 16 ? 2 : 1, max: Math.max(1, Math.ceil(a / 6)) }),
  },
  paneel: {
    name: 'LED-paneel 60×60', desc: 'Armstrongi ripplakke', col: 'paneelid-60x60', unit: ['paneel', 'paneeli'],
    exclude: /62×62/,
    limits: (a) => ({ min: Math.max(1, Math.ceil(a / 12)), max: Math.ceil(a / 2.5) + 1 }),
  },
  karg: {
    name: 'Kärgvalgusti', desc: 'Kuusnurksed moodulid lakke', col: 'laevalgustid-kargvalgustid', unit: ['komplekt', 'komplekti'],
    exclude: null,
    limits: () => ({ min: 1, max: 6 }),
  },
  prozektor: {
    name: 'Prožektorid', desc: 'Õuealale ja fassaadile', col: 'floodlights', unit: ['prožektor', 'prožektorit'],
    exclude: /kaasaskantav|aku|maapiik|tarvik|statiiv/i,
    limits: (a) => ({ min: Math.max(1, Math.ceil(a / 200)), max: Math.ceil(a / 8) + 1 }),
  },
  pirn: {
    name: 'LED-pirnid', desc: '', col: null, unit: ['pirn', 'pirni'],
    exclude: null,
    limits: (a) => ({ min: 1, max: Math.ceil(a / 2) + 4 }),
  },
  toru: {
    name: 'LED-torud', desc: '', col: null, unit: ['toru', 'toru'],
    exclude: null,
    limits: (a) => ({ min: Math.max(1, Math.ceil(a / 12)), max: Math.ceil(a / 1.5) + 2 }),
  },
  lineaar: {
    name: 'Lineaarvalgustid', desc: '', col: null, unit: ['valgusti', 'valgustit'],
    exclude: null,
    limits: (a) => ({ min: Math.max(1, Math.ceil(a / 14)), max: Math.ceil(a / 2) + 2 }),
  },
  sein: {
    name: 'Seinavalgustid', desc: '', col: null, unit: ['seinavalgusti', 'seinavalgustit'],
    exclude: null, share: 0.35,
    limits: (a) => ({ min: 1, max: Math.ceil(a / 4) + 2 }),
  },
  korglao: {
    name: 'Kõrglaovalgusti', desc: 'Kõrgetele lagedele', col: 'toostus-korglao', unit: ['valgusti', 'valgustit'],
    exclude: null,
    limits: (a) => ({ min: Math.max(1, Math.ceil(a / 150)), max: Math.ceil(a / 20) + 1 }),
  },
};

export const TONES = {
  soe: { name: 'Soe valge', hint: '2700–3000 K' },
  neutraalne: { name: 'Neutraalne', hint: '4000 K' },
  kylm: { name: 'Külm valge', hint: '5000–6500 K' },
};

export const HEIGHTS = [2.5, 2.7, 3, 3.5, 4.5, 6, 8, 10];

export function heightFactor(h) {
  if (h <= 2.8) return 1;
  if (h <= 3.2) return 1.1;
  if (h <= 3.8) return 1.2;
  if (h <= 5) return 1.3;
  if (h <= 7) return 1.45;
  return 1.6;
}

const money = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');
const moneyRound = (euros) => '€' + Math.round(euros).toLocaleString('et-EE');
const fmtInt = (n) => Math.round(n).toLocaleString('et-EE');
const fmtNum = (n) => (Math.round(n * 10) / 10).toString().replace('.', ',');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

function toneOf(p) {
  if (/CCT|muudetav/i.test(p.t)) return 'all';
  const k = parseFloat(p.k) || 0;
  if (!k) return 'all';
  if (k <= 3300) return 'soe';
  if (k < 5000) return 'neutraalne';
  return 'kylm';
}

function ipOf(p) {
  const m = String(p.ip || '').match(/IP\s?(\d)(\d)/i);
  return m ? parseInt(m[1] + m[2], 10) : 20;
}

function isDimmable(p) {
  return /hämardatav|pult|mälu/i.test(p.t);
}

function featuresOf(p) {
  const t = p.t;
  const f = [];
  if (/CCT|muudetav/i.test(t)) f.push({ k: 'cct', label: 'Muudetav valguse toon' });
  if (/pult/i.test(t)) f.push({ k: 'remote', label: 'Puldiga' });
  if (/mälu/i.test(t)) f.push({ k: 'dim', label: 'Mälufunktsioon' });
  else if (/hämardatav/i.test(t)) f.push({ k: 'dim', label: 'Hämardatav' });
  if (/RGB/.test(t)) f.push({ k: 'rgb', label: 'RGB-meeleoluvalgus' });
  if (/[UR][GR][RG]\s?<\s?19/i.test(t)) f.push({ k: 'ugr', label: 'Pimestamisvaba UGR<19' });
  if (/T[UÜ]V/i.test(t)) f.push({ k: 'tuv', label: 'TÜV-sertifikaat' });
  if (/andur|PIR|mikrolaine|microwave/i.test(t)) f.push({ k: 'sensor', label: 'Liikumisanduriga' });
  const lmw = t.match(/(\d{3})\s?lm\/W/);
  if (lmw && +lmw[1] >= 140) f.push({ k: 'eff', label: `Säästlik ${lmw[1]} lm/W` });
  if (/puit|walnut/i.test(t)) f.push({ k: 'wood', label: 'Puitdetailiga' });
  if (/garantii/i.test(t)) f.push({ k: 'warranty', label: 'Pikem garantii' });
  if (/draiver/i.test(t)) f.push({ k: 'driver', label: 'Koos draiveriga' });
  if (/virvendusvaba/i.test(t)) f.push({ k: 'flicker', label: 'Virvendusvaba' });
  const ip = ipOf(p);
  if (ip >= 44) f.push({ k: 'ip', label: `Niiskuskindel IP${ip}` });
  return f;
}

const ROOM_BONUS = {
  kontor: { ugr: 0.4 },
  kodukontor: { ugr: 0.3, cct: 0.1 },
  vannituba: { ip: 0.2 },
  ladu: { eff: 0.35 },
  garaaz: { eff: 0.2 },
  elutuba: { remote: 0.15, dim: 0.1 },
  magamistuba: { remote: 0.15, dim: 0.15 },
  kook: { cct: 0.1 },
  oue: { sensor: 0.35 },
  parkla: { sensor: 0.2, eff: 0.2 },
  laoplats: { eff: 0.3 },
  spordiplats: { eff: 0.3 },
};

/** Arvutab ruumi valgusvajaduse. */
export function needFor(state) {
  const room = ROOMS[state.room];
  const area = state.l * state.w;
  return { area, need: area * room.lm * heightFactor(state.h) };
}

/** Arvutab ühe toote jaoks koguse ja hinnangu. Tagastab null, kui toode ei sobi. */
function evaluate(p, state, area, need, strict = true) {
  const room = ROOMS[state.room];
  const type = TYPES[state.type];
  const lm = parseFloat(p.lm);
  if (!(lm > 0) || !p.a) return null;
  if (strict && type.exclude && type.exclude.test(p.t)) return null;
  const tone = toneOf(p);
  if (strict && tone !== 'all' && tone !== state.tone) return null;
  if (strict && room.ip && ipOf(p) < room.ip) return null;
  const lim = type.limits(area);
  need *= type.share || 1;
  let qty = Math.max(1, Math.ceil((need * 0.92) / lm));
  const tooWeak = qty > lim.max;
  if (strict && tooWeak) return null;
  const minQ = strict ? lim.min : Math.max(1, Math.ceil(lim.min * 0.67));
  if (qty < minQ) qty = minQ;
  const dimm = isDimmable(p);
  const over = (qty * lm) / need;
  const overMax = (room.work ? 2.6 : dimm ? 2.3 : 1.75) + (strict ? 0 : 0.3);
  const tooStrong = over > overMax;
  if (strict && tooStrong) return null;
  const w = parseFloat(p.w) || 0;
  const energy = (w * qty * room.hours) / 1000 * PRICE_KWH;
  return { p, qty, lm, over, total: qty * p.p, feats: featuresOf(p), energy, dimm, tooWeak, tooStrong, lim };
}

function scoreOf(o, roomKey) {
  const bonus = ROOM_BONUS[roomKey] || {};
  let mult = 1 + 0.12 * o.feats.length;
  for (const f of o.feats) mult += bonus[f.k] || 0;
  const pr = parseFloat(o.p.pr) || 0;
  return o.total * mult * (1 + 0.15 * pr);
}

/** Valib soodsaima ja „Meie soovitus“ valiku ning teised sobivad. */
export function recommend(products, state) {
  const { area, need } = needFor(state);
  const opts = [];
  for (const p of products) {
    const o = evaluate(p, state, area, need, true);
    if (o) opts.push(o);
  }
  if (!opts.length) return { area, need, opts, cheapest: null, best: null, others: [] };

  opts.sort((a, b) => a.total - b.total || b.feats.length - a.feats.length);
  const cheapest = opts[0];
  const cap = Math.max(cheapest.total * 3.5, cheapest.total + 5000);
  const capEff = cheapest.total * 6;
  let best = null;
  let bestScore = -1;
  for (const o of opts) {
    if (o === cheapest) continue;
    if (o.qty > cheapest.qty + Math.max(2, Math.ceil(cheapest.qty * 0.25))) continue;
    const room = ROOMS[state.room];
    const realFeats = o.feats.filter((f) => (f.k !== 'ip' || room.ip || room.outdoor) && !cheapest.feats.some((c) => c.k === f.k));
    const yearlySave = cheapest.energy - o.energy;
    if (!realFeats.length && yearlySave < 15) continue;
    if (o.over > Math.max(1.6, cheapest.over + 0.25)) continue;
    const savings3y = yearlySave * 3 * 100;
    const limit = savings3y > 0 && savings3y >= o.total - cheapest.total ? capEff : cap;
    if (o.total > limit) continue;
    const s = scoreOf(o, state.room);
    if (s > bestScore) {
      bestScore = s;
      best = o;
    }
  }

  const sig = (o) => [o.lm, o.qty, o.p.p, o.feats.map((f) => f.k).join('.')].join('|');
  const shown = new Set([cheapest, best].filter(Boolean).map(sig));
  const others = [];
  const ranked = opts
    .filter((o) => o !== cheapest && o !== best)
    .sort((a, b) => scoreOf(b, state.room) - scoreOf(a, state.room));
  for (const o of ranked) {
    const s = sig(o);
    if (shown.has(s)) continue;
    shown.add(s);
    others.push(o);
    if (others.length >= 4) break;
  }
  return { area, need, opts, cheapest, best, others };
}

/** Paigutab valgustid ruumi ruudustikku. */
export function layout(n, L, W) {
  if (n <= 0) return { cols: 0, rows: 0, pts: [] };
  let cols = Math.max(1, Math.round(Math.sqrt((n * L) / W)));
  cols = Math.min(cols, n);
  let rows = Math.ceil(n / cols);
  const pts = [];
  let left = n;
  for (let r = 0; r < rows; r++) {
    const inRow = Math.min(cols, left);
    for (let c = 0; c < inRow; c++) {
      pts.push({ x: ((c + 0.5) / inRow) * L, y: ((r + 0.5) / rows) * W });
    }
    left -= inRow;
  }
  return { cols, rows, pts, dx: L / cols, dy: W / rows };
}

const GLOW = { soe: '#FFC46B', neutraalne: '#FFE7A6', kylm: '#CFE3FF' };

function roomSvg(L, W, n, tone, isSingle) {
  const maxW = 520;
  const maxH = 300;
  const s = Math.min(maxW / L, maxH / W);
  const w = Math.max(140, L * s);
  const h = Math.max(90, W * s);
  const pad = 28;
  const lay = layout(n, L, W);
  const glowR = isSingle ? Math.min(w, h) * 0.55 : Math.min((lay.dx || L) * s, (lay.dy || W) * s) * 0.85;
  const color = GLOW[tone] || GLOW.neutraalne;
  const dots = lay.pts
    .map((p) => {
      const cx = pad + (p.x / L) * w;
      const cy = pad + (p.y / W) * h;
      return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${glowR.toFixed(1)}" fill="url(#nlcGlow)"/>`;
    })
    .join('');
  const cores = lay.pts
    .map((p) => {
      const cx = pad + (p.x / L) * w;
      const cy = pad + (p.y / W) * h;
      return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="5.5" fill="#fff" stroke="#181A1C" stroke-width="1.5"/>`;
    })
    .join('');
  const vw = w + pad * 2;
  const vh = h + pad * 2;
  return `<svg class="nlc-room" viewBox="0 0 ${vw.toFixed(0)} ${vh.toFixed(0)}" role="img" aria-label="Ruumi plaan: ${n} valgustit ${fmtNum(L)} × ${fmtNum(W)} m ruumis">
    <defs>
      <radialGradient id="nlcGlow"><stop offset="0" stop-color="${color}" stop-opacity=".85"/><stop offset=".55" stop-color="${color}" stop-opacity=".28"/><stop offset="1" stop-color="${color}" stop-opacity="0"/></radialGradient>
      <clipPath id="nlcClip"><rect x="${pad}" y="${pad}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="10"/></clipPath>
    </defs>
    <rect x="${pad}" y="${pad}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="10" fill="#FAFAF8" stroke="#D6D6D1" stroke-width="1.5"/>
    <g clip-path="url(#nlcClip)">${dots}</g>
    ${cores}
    <text x="${(pad + w / 2).toFixed(1)}" y="${(pad - 10).toFixed(1)}" text-anchor="middle" class="nlc-room__dim">${fmtNum(L)} m</text>
    <text x="${(pad - 10).toFixed(1)}" y="${(pad + h / 2).toFixed(1)}" text-anchor="middle" class="nlc-room__dim" transform="rotate(-90 ${(pad - 10).toFixed(1)} ${(pad + h / 2).toFixed(1)})">${fmtNum(W)} m</text>
  </svg>`;
}

/* ---------- Andmed ja ostukorv ---------- */

const cache = new Map();
export function loadProducts(col) {
  if (!cache.has(col)) {
    const url = `${ROOT}collections/${col}?view=nl-calc`;
    cache.set(
      col,
      fetch(url, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then((t) => JSON.parse(t).products || [])
        .catch((e) => {
          cache.delete(col);
          throw e;
        })
    );
  }
  return cache.get(col);
}

let eventsModule;
async function getEvents() {
  if (eventsModule === undefined) {
    try {
      eventsModule = await import('@shopify/events');
    } catch (e) {
      eventsModule = null;
    }
  }
  return eventsModule;
}

/** Lisab mitu rida ostukorvi ja annab teemale teada (päise number ja külgpaneel uuenevad). */
export async function addToCart(origin, items) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const ev = await getEvents();
  let deferred = null;
  if (ev && ev.CartLinesUpdateEvent) {
    try {
      deferred = ev.CartLinesUpdateEvent.createPromise();
      origin.dispatchEvent(
        new ev.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (e) {
      deferred = null;
    }
  }
  try {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Lisamine ebaõnnestus');
    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (deferred) {
      deferred.resolve({
        cart: ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: 'nl-light-calc',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          sections: data.sections,
          didError: false,
        },
      });
    }
    return cart;
  } catch (err) {
    if (deferred) deferred.reject(err);
    throw err;
  }
}

async function cartTotal() {
  try {
    const c = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    return c.total_price || 0;
  } catch (e) {
    return 0;
  }
}

/* ---------- Ikoonid ---------- */

const ICONS = {
  sofa: '<path d="M4 11V8a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v3"/><path d="M2 13a2 2 0 0 1 4 0v2h12v-2a2 2 0 0 1 4 0v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/><path d="M5 18v2M19 18v2"/>',
  bed: '<path d="M3 18V6M3 14h18v4M21 18v-6a3 3 0 0 0-3-3h-7v5"/><circle cx="7" cy="11" r="2"/>',
  pot: '<path d="M4 10h16v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z"/><path d="M2 10h20M9 4v2M12 3v3M15 4v2"/>',
  bath: '<path d="M3 12h18v3a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5z"/><path d="M6 12V6a2 2 0 0 1 4 0M7 20l-1 2M17 20l1 2"/>',
  door: '<path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M3 21h18"/><circle cx="14.5" cy="12" r=".8" fill="currentColor"/>',
  star: '<path d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 16.6 6.6 19.5l1.2-6-4.5-4.2 6.1-.7z"/>',
  laptop: '<rect x="5" y="5" width="14" height="10" rx="1.5"/><path d="M2 19h20l-2-4H4z"/>',
  office: '<rect x="4" y="3" width="16" height="18" rx="1.5"/><path d="M8 7h2M14 7h2M8 11h2M14 11h2M8 15h2M14 15h2M10 21v-3h4v3"/>',
  bag: '<path d="M5 8h14l-1 13H6z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
  car: '<path d="M3 16v-3l2-5a2 2 0 0 1 2-1h10a2 2 0 0 1 2 1l2 5v3a1 1 0 0 1-1 1h-1M3 16a1 1 0 0 0 1 1h1M5 13h14"/><circle cx="7.5" cy="17" r="1.8"/><circle cx="16.5" cy="17" r="1.8"/>',
  box: '<path d="M3 8l9-5 9 5v8l-9 5-9-5z"/><path d="M3 8l9 5 9-5M12 13v8"/>',
  tree: '<path d="M12 22v-5"/><path d="M12 2 5.5 11H9l-3.5 6h13L15 11h3.5z"/>',
  parking: '<rect x="3.5" y="3.5" width="17" height="17" rx="3"/><path d="M9.5 17V7h3.5a3 3 0 0 1 0 6H9.5"/>',
  crane: '<path d="M4 21V5l10-2v4M4 7h16M14 7v5M12 12h4v3h-4zM2 21h8"/>',
  ball: '<circle cx="12" cy="12" r="9"/><path d="M3.5 9.5c5 1.5 12 1.5 17 0M3.5 14.5c5-1.5 12-1.5 17 0M12 3v18"/>',
};
const icon = (name) =>
  `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ''}</svg>`;

/* ---------- Täiskalkulaator ---------- */

class NlLightCalc extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._abort = new AbortController();
    this.state = this.readState();
    this.renderForm();
    this.addEventListener('change', (e) => this.onChange(e), { signal: this._abort.signal });
    this.addEventListener('input', (e) => this.onInput(e), { signal: this._abort.signal });
    this.addEventListener('click', (e) => this.onClick(e), { signal: this._abort.signal });
    this.cartCents = 0;
    cartTotal().then((c) => {
      this.cartCents = c;
      this.updateShipping();
    });
    this.update();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._ready = false;
  }

  readState() {
    const q = new URLSearchParams(location.search);
    const roomKey = ROOMS[q.get('ruum')] ? q.get('ruum') : this.dataset.room && ROOMS[this.dataset.room] ? this.dataset.room : 'elutuba';
    const room = ROOMS[roomKey];
    const num = (v, d) => {
      const n = parseFloat(String(v ?? '').replace(',', '.'));
      return n > 0 && n < 500 ? n : d;
    };
    let type = q.get('tyyp') || this.dataset.type;
    const pageTypes = room.types.filter((k) => TYPES[k].col);
    if (!pageTypes.includes(type)) type = pageTypes[0];
    const tone = TONES[q.get('toon')] ? q.get('toon') : room.tone;
    return { room: roomKey, type, tone, l: num(q.get('p'), room.l), w: num(q.get('l'), room.w), h: num(q.get('k'), room.h) };
  }

  writeUrl() {
    const s = this.state;
    const q = new URLSearchParams(location.search);
    q.set('ruum', s.room);
    q.set('tyyp', s.type);
    q.set('toon', s.tone);
    q.set('p', String(s.l));
    q.set('l', String(s.w));
    q.set('k', String(s.h));
    history.replaceState(null, '', `${location.pathname}?${q.toString()}`);
  }

  renderForm() {
    const s = this.state;
    const rooms = Object.entries(ROOMS)
      .map(
        ([k, r]) => `<label class="nlc-chip nlc-chip--room">
          <input type="radio" name="room" value="${k}" ${k === s.room ? 'checked' : ''}>
          <span class="nlc-chip__inner">${icon(r.icon)}<span>${esc(r.name)}</span></span>
        </label>`
      )
      .join('');
    const heights = HEIGHTS.map((h) => `<option value="${h}" ${h === s.h ? 'selected' : ''}>${fmtNum(h)} m${h >= 10 ? '+' : ''}</option>`).join('');
    const tones = Object.entries(TONES)
      .map(
        ([k, t]) => `<label class="nlc-chip nlc-chip--tone">
          <input type="radio" name="tone" value="${k}" ${k === s.tone ? 'checked' : ''}>
          <span class="nlc-chip__inner"><span class="nlc-dot nlc-dot--${k}" aria-hidden="true"></span><span>${esc(t.name)}<small>${esc(t.hint)}</small></span></span>
        </label>`
      )
      .join('');
    this.querySelector('[data-form]').innerHTML = `
      <fieldset class="nlc-step">
        <legend class="nlc-step__title"><span class="nlc-step__num">1</span>Milline ruum?</legend>
        <div class="nlc-rooms">${rooms}</div>
      </fieldset>
      <fieldset class="nlc-step">
        <legend class="nlc-step__title"><span class="nlc-step__num">2</span>Ruumi mõõdud</legend>
        <div class="nlc-dims">
          <label class="nlc-field"><span>Pikkus</span><span class="nlc-input"><input type="number" name="l" min="0.5" max="200" step="0.1" inputmode="decimal" value="${s.l}"><em>m</em></span></label>
          <span class="nlc-dims__x" aria-hidden="true">×</span>
          <label class="nlc-field"><span>Laius</span><span class="nlc-input"><input type="number" name="w" min="0.5" max="200" step="0.1" inputmode="decimal" value="${s.w}"><em>m</em></span></label>
          <label class="nlc-field nlc-field--h"><span>${ROOMS[s.room].outdoor ? 'Paigalduskõrgus' : 'Lae kõrgus'}</span><span class="nlc-input nlc-input--select"><select name="h">${heights}</select></span></label>
        </div>
        <p class="nlc-area" data-area></p>
      </fieldset>
      <fieldset class="nlc-step">
        <legend class="nlc-step__title"><span class="nlc-step__num">3</span>Valgusti tüüp</legend>
        <div class="nlc-types" data-types></div>
      </fieldset>
      <fieldset class="nlc-step">
        <legend class="nlc-step__title"><span class="nlc-step__num">4</span>Valguse toon</legend>
        <div class="nlc-tones">${tones}</div>
      </fieldset>
      <a href="#nlc-result" class="nlc-btn nlc-btn--ghost nlc-jump">Vaata soovitust</a>`;
    this.renderTypes();
  }

  renderTypes() {
    const s = this.state;
    const room = ROOMS[s.room];
    this.querySelector('[data-types]').innerHTML = room.types
      .filter((k) => TYPES[k].col)
      .map((k) => {
        const t = TYPES[k];
        return `<label class="nlc-chip nlc-chip--type">
          <input type="radio" name="type" value="${k}" ${k === s.type ? 'checked' : ''}>
          <span class="nlc-chip__inner"><strong>${esc(t.name)}</strong><small>${esc(t.desc)}</small></span>
        </label>`;
      })
      .join('');
  }

  onChange(e) {
    const t = e.target;
    if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return;
    if (t.closest('[data-result]')) return;
    const s = this.state;
    if (t.name === 'room') {
      const r = ROOMS[t.value];
      s.room = t.value;
      s.l = r.l;
      s.w = r.w;
      s.h = r.h;
      s.tone = r.tone;
      const pageTypes = r.types.filter((k) => TYPES[k].col);
      if (!pageTypes.includes(s.type)) s.type = pageTypes[0];
      this.renderForm();
    } else if (t.name === 'type') s.type = t.value;
    else if (t.name === 'tone') s.tone = t.value;
    else if (t.name === 'h') s.h = parseFloat(t.value);
    else if (t.name === 'l' || t.name === 'w') this.readDims();
    else return;
    this.update();
  }

  onInput(e) {
    const t = e.target;
    if (t instanceof HTMLInputElement && (t.name === 'l' || t.name === 'w') && !t.closest('[data-result]')) {
      clearTimeout(this._t);
      this._t = setTimeout(() => {
        if (this.readDims()) this.update();
      }, 350);
    }
    if (t instanceof HTMLInputElement && t.matches('[data-qty-input]')) {
      const card = t.closest('[data-card]');
      const q = Math.max(1, Math.min(999, parseInt(t.value, 10) || 1));
      this.setQty(card, q, false);
    }
  }

  readDims() {
    const l = parseFloat(String(this.querySelector('input[name="l"]')?.value || '').replace(',', '.'));
    const w = parseFloat(String(this.querySelector('input[name="w"]')?.value || '').replace(',', '.'));
    if (l > 0 && w > 0 && l <= 200 && w <= 200) {
      this.state.l = l;
      this.state.w = w;
      return true;
    }
    return false;
  }

  onClick(e) {
    const btn = e.target instanceof Element ? e.target.closest('button, a') : null;
    if (!btn) return;
    if (btn.matches('[data-qty]')) {
      const card = btn.closest('[data-card]');
      const cur = parseInt(card.querySelector('[data-qty-input]').value, 10) || 1;
      this.setQty(card, Math.max(1, cur + (btn.dataset.qty === '+' ? 1 : -1)), true);
    } else if (btn.matches('[data-add]')) {
      this.add(btn);
    } else if (btn.matches('.nlc-jump')) {
      e.preventDefault();
      this.querySelector('#nlc-result')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    } else if (btn.matches('[data-switch-type]')) {
      this.state.type = btn.dataset.switchType;
      this.renderTypes();
      this.update();
    }
  }

  setQty(card, q, writeInput) {
    if (!card) return;
    const unit = parseInt(card.dataset.price, 10);
    if (writeInput) card.querySelector('[data-qty-input]').value = String(q);
    card.dataset.qty = String(q);
    card.querySelector('[data-unit]').textContent = `${q} × ${money(unit)}`;
    card.querySelector('[data-total]').textContent = money(unit * q);
    const btn = card.querySelector('[data-add]');
    if (btn && !btn.disabled) btn.textContent = `Lisa korvi – ${q} tk`;
    if (card.matches('[data-primary]')) this.updateShipping();
  }

  async add(btn) {
    const card = btn.closest('[data-card]');
    if (!card) return;
    const id = parseInt(card.dataset.variant, 10);
    const quantity = parseInt(card.dataset.qty, 10) || 1;
    const label = btn.textContent;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Lisan…';
    const live = this.querySelector('[data-live]');
    try {
      const cart = await addToCart(this, [{ id, quantity }]);
      this.cartCents = cart.total_price || this.cartCents;
      btn.textContent = 'Lisatud ostukorvi ✓';
      btn.classList.add('is-added');
      if (live) live.textContent = `${quantity} tk lisati ostukorvi.`;
      this.updateShipping();
    } catch (err) {
      btn.textContent = 'Ei õnnestunud – proovi uuesti';
      if (live) live.textContent = 'Ostukorvi lisamine ebaõnnestus.';
    }
    setTimeout(() => {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-added');
      btn.textContent = label;
    }, 2600);
  }

  async update() {
    const s = this.state;
    const room = ROOMS[s.room];
    const { area, need } = needFor(s);
    const areaEl = this.querySelector('[data-area]');
    if (areaEl) areaEl.innerHTML = `Pindala <strong>${fmtNum(area)} m²</strong>`;
    this.writeUrl();

    const res = this.querySelector('[data-result]');
    const token = (this._token = Symbol('u'));
    const lux = room.lux ? ` · ≈ ${room.lux} lx (EN 12464-${room.outdoor ? 2 : 1})` : '';
    res.querySelector('[data-summary]').innerHTML = `
      <p class="nlc-summary__label">${esc(room.name)} vajab umbes</p>
      <p class="nlc-summary__value">${fmtInt(need)}<span> luumenit</span></p>
      <p class="nlc-summary__sub">${fmtNum(area)} m² · ${room.lm} lm/m²${lux}${heightFactor(s.h) > 1 ? ` · ${room.outdoor ? 'paigalduskõrguse' : 'kõrge lae'} lisa +${Math.round((heightFactor(s.h) - 1) * 100)}%` : ''}</p>`;
    const list = res.querySelector('[data-options]');
    list.innerHTML = '<div class="nlc-loading" role="status">Otsin sobivaid valgusteid…</div>';

    let products;
    try {
      products = await loadProducts(TYPES[s.type].col);
    } catch (e) {
      if (token !== this._token) return;
      list.innerHTML = '<p class="nlc-empty">Tooteid ei õnnestunud laadida. Värskenda lehte ja proovi uuesti.</p>';
      return;
    }
    if (token !== this._token) return;
    const r = recommend(products, s);
    this.renderResult(r, products);
  }

  renderResult(r, products) {
    const s = this.state;
    const type = TYPES[s.type];
    const room = ROOMS[s.room];
    const res = this.querySelector('[data-result]');
    const list = res.querySelector('[data-options]');
    const planEl = res.querySelector('[data-plan]');
    const main = r.best || r.cheapest;

    if (!main) {
      const others = room.types.filter((k) => k !== s.type && TYPES[k].col);
      planEl.innerHTML = roomSvg(s.l, s.w, 0, s.tone, true);
      list.innerHTML = `<div class="nlc-empty"><p>Selle valgusti tüübi ja valguse tooniga ei leidnud sellele ruumile sobivat lahendust.</p>
        ${others.length ? `<p>Proovi: ${others.map((k) => `<button type="button" class="nlc-link" data-switch-type="${k}">${esc(TYPES[k].name)}</button>`).join(' · ')}</p>` : ''}</div>`;
      return;
    }

    const lay = layout(main.qty, s.l, s.w);
    const single = main.qty === 1;
    const spacing = single ? '' : ` umbes ${fmtNum(Math.max(lay.dx, lay.dy))} m vahedega`;
    planEl.innerHTML = `${roomSvg(s.l, s.w, main.qty, s.tone, single)}
      <p class="nlc-plan__note">${main.qty} ${esc(type.unit[main.qty === 1 ? 0 : 1])}${spacing}${single ? (room.outdoor ? ' ala keskele' : ' lae keskele') : ''}. Kokku ${fmtInt(main.qty * main.lm)} lm.</p>`;

    const cards = [];
    if (r.best) cards.push(this.card(r.best, 'best', r.cheapest));
    if (r.cheapest) cards.push(this.card(r.cheapest, 'cheap', r.cheapest));
    let html = `<div class="nlc-cards">${cards.join('')}</div>`;
    html += `<p class="nlc-ship" data-ship></p>`;
    if (r.others.length) {
      html += `<details class="nlc-more"><summary>Teised sobivad valikud (${r.others.length})</summary><div class="nlc-more__list">${r.others.map((o) => this.row(o)).join('')}</div></details>`;
    }
    html += `<p class="nlc-fine">Arvutus on orienteeriv ja eeldab heledaid seinu ning ühtlast paigutust. Kõik valgustid saad tagastada 14 päeva jooksul.</p>`;
    list.innerHTML = html;
    this.updateShipping();
  }

  card(o, kind, cheapest) {
    const p = o.p;
    const isBest = kind === 'best';
    const feats = o.feats.slice(0, 3).map((f) => `<li>${esc(f.label)}</li>`).join('');
    let reason = '';
    if (isBest) {
      const top = o.feats.filter((f) => !cheapest || !cheapest.feats.some((c) => c.k === f.k)).slice(0, 2).map((f) => (/^.[a-zäöüõ]/.test(f.label) ? f.label.charAt(0).toLowerCase() + f.label.slice(1) : f.label));
      const save = cheapest ? cheapest.energy - o.energy : 0;
      const parts = [];
      if (save >= 15) parts.push(`Säästab elektrit umbes ${moneyRound(save)} aastas võrreldes soodsaimaga.`);
      else if (top.length) parts.push(`Parem valik: ${top.join(' ja ')}.`);
      if (cheapest && o.qty < cheapest.qty) parts.push(`Vaja ainult ${o.qty} tk (soodsaimal ${cheapest.qty}) – vähem paigaldustööd.`);
      reason = parts.join(' ');
    } else {
      reason = 'Täidab vajaduse kõige väiksema hinnaga.';
    }
    const energy = o.energy >= 1 ? `<span>Elekter ≈ ${moneyRound(o.energy)}/aastas</span>` : '';
    return `<article class="nlc-card ${isBest ? 'nlc-card--best' : ''}" data-card ${isBest ? 'data-primary' : ''} data-variant="${p.id}" data-price="${p.p}" data-qty="${o.qty}">
      <p class="nlc-card__badge">${isBest ? 'Meie soovitus' : 'Soodsaim'}</p>
      <div class="nlc-card__body">
        <a class="nlc-card__img" href="${esc(p.u)}">${p.img ? `<img src="${esc(p.img)}" alt="" width="160" height="160" loading="lazy">` : ''}</a>
        <div class="nlc-card__info">
          <a class="nlc-card__title" href="${esc(p.u)}">${esc(p.t)}</a>
          <p class="nlc-card__meta">${fmtInt(o.lm)} lm${p.w ? ` · ${fmtNum(parseFloat(p.w))} W` : ''} ${energy}</p>
          ${feats ? `<ul class="nlc-feats">${feats}</ul>` : ''}
        </div>
      </div>
      ${reason ? `<p class="nlc-card__reason">${esc(reason)}</p>` : ''}
      <div class="nlc-card__foot">
        <div class="nlc-qty">
          <button type="button" data-qty="-" aria-label="Vähenda kogust">−</button>
          <input type="number" min="1" max="999" value="${o.qty}" data-qty-input aria-label="Kogus">
          <button type="button" data-qty="+" aria-label="Suurenda kogust">+</button>
        </div>
        <div class="nlc-card__price"><span data-unit>${o.qty} × ${money(p.p)}</span><strong data-total>${money(o.total)}</strong></div>
      </div>
      <button type="button" class="nlc-btn ${isBest ? 'nlc-btn--primary' : 'nlc-btn--outline'}" data-add>Lisa korvi – ${o.qty} tk</button>
    </article>`;
  }

  row(o) {
    const p = o.p;
    return `<div class="nlc-row" data-card data-variant="${p.id}" data-price="${p.p}" data-qty="${o.qty}">
      <a class="nlc-row__img" href="${esc(p.u)}">${p.img ? `<img src="${esc(p.img)}" alt="" width="64" height="64" loading="lazy">` : ''}</a>
      <div class="nlc-row__info"><a href="${esc(p.u)}">${esc(p.t)}</a><span data-unit>${o.qty} × ${money(p.p)}</span></div>
      <strong class="nlc-row__total" data-total>${money(o.total)}</strong>
      <input type="hidden" data-qty-input value="${o.qty}">
      <button type="button" class="nlc-btn nlc-btn--small" data-add>Lisa korvi – ${o.qty} tk</button>
    </div>`;
  }

  updateShipping() {
    const el = this.querySelector('[data-ship]');
    if (!el) return;
    const card = this.querySelector('[data-primary]') || this.querySelector('[data-card]');
    if (!card) {
      el.textContent = '';
      return;
    }
    const sum = parseInt(card.dataset.price, 10) * (parseInt(card.dataset.qty, 10) || 1);
    const total = this.cartCents + sum;
    if (total >= FREE_SHIPPING_CENTS) {
      el.innerHTML = '<span class="nlc-ship__ok">✓</span> Selle valikuga on tarne üle Eesti tasuta.';
    } else {
      el.innerHTML = `Lisa veel <strong>${money(FREE_SHIPPING_CENTS - total)}</strong> ja tarne on tasuta (alates €90).`;
    }
  }
}

/* ---------- Tootelehe väike kalkulaator ---------- */

class NlCalcProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._abort = new AbortController();
    const d = this.dataset;
    this.p = { id: parseInt(d.variant, 10), p: parseInt(d.price, 10), lm: parseFloat(d.lm), w: parseFloat(d.w) || 0, t: d.title || '', a: d.available === 'true', k: d.kelvin, ip: d.ip };
    this.type = d.type;
    const roomSel = this.querySelector('select[name="room"]');
    const opts = Object.entries(ROOMS).filter(([, r]) => r.types.includes(this.type));
    roomSel.innerHTML = opts.map(([k, r]) => `<option value="${k}">${esc(r.name)}</option>`).join('');
    const hSel = this.querySelector('select[name="h"]');
    hSel.innerHTML = HEIGHTS.map((h) => `<option value="${h}">${fmtNum(h)} m${h >= 10 ? '+' : ''}</option>`).join('');
    this.applyRoom(opts[0][0]);
    this.addEventListener('change', (e) => this.onChange(e), { signal: this._abort.signal });
    this.addEventListener('input', (e) => {
      if (e.target instanceof HTMLInputElement) {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.update(), 300);
      }
    }, { signal: this._abort.signal });
    this.querySelector('[data-add]')?.addEventListener('click', (e) => this.add(e.currentTarget), { signal: this._abort.signal });
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._ready = false;
  }

  applyRoom(key) {
    const r = ROOMS[key];
    this.querySelector('select[name="room"]').value = key;
    this.querySelector('input[name="l"]').value = String(r.l);
    this.querySelector('input[name="w"]').value = String(r.w);
    this.querySelector('select[name="h"]').value = String(r.h);
    this.update();
  }

  onChange(e) {
    const t = e.target;
    if (t instanceof HTMLSelectElement && t.name === 'room') this.applyRoom(t.value);
    else this.update();
  }

  update() {
    const room = this.querySelector('select[name="room"]').value;
    const l = parseFloat(String(this.querySelector('input[name="l"]').value).replace(',', '.'));
    const w = parseFloat(String(this.querySelector('input[name="w"]').value).replace(',', '.'));
    const h = parseFloat(this.querySelector('select[name="h"]').value);
    const out = this.querySelector('[data-out]');
    const btn = this.querySelector('[data-add]');
    const link = this.querySelector('[data-full]');
    if (!(l > 0 && w > 0)) {
      out.textContent = 'Sisesta ruumi mõõdud.';
      return;
    }
    const state = { room, type: this.type, tone: ROOMS[room].tone, l, w, h };
    const { area, need } = needFor(state);
    const o = evaluate(this.p, state, area, need, false);
    if (!o) {
      out.textContent = 'Selle toote andmetega ei saa arvutust teha.';
      return;
    }
    this.qty = o.qty;
    let note = '';
    if (o.tooWeak) note = `<span class="nlc-pnote">Selle ruumi jaoks on see valgusti üsna nõrk. Vaata kalkulaatorist võimsamaid valikuid.</span>`;
    else if (o.tooStrong) note = o.dimm ? `<span class="nlc-pnote nlc-pnote--ok">Annab rohkem valgust kui vaja, aga on hämardatav, nii et saad heleduse ise sobivaks seada.</span>` : `<span class="nlc-pnote">Annab sellele ruumile rohkem valgust kui vaja. Kaalu väiksemat võimsust.</span>`;
    const share = TYPES[this.type].share;
    const shareNote = share ? `<span class="nlc-pout__need">Seinavalgustid annavad tavaliselt lisavalgust: arvestame neilt umbes ${Math.round(share * 100)}% ehk <strong>${fmtInt(need * share)} lm</strong>.</span>` : '';
    out.innerHTML = `<span class="nlc-pout__need">${fmtNum(area)} m² ${esc(ROOMS[room].name.toLowerCase())} vajab umbes <strong>${fmtInt(need)} lm</strong></span>${shareNote}
      <span class="nlc-pout__qty">Sul on vaja <strong>${o.qty} tk</strong> <span class="nlc-pout__lm">(kokku ${fmtInt(o.qty * o.lm)} lm)</span></span>
      <span class="nlc-pout__sum">${o.qty} × ${money(this.p.p)} = <strong>${money(o.total)}</strong></span>${note}`;
    if (btn) {
      btn.textContent = `Lisa ${o.qty} tk korvi`;
      btn.disabled = !this.p.a;
    }
    if (link) {
      if (!TYPES[this.type].col) {
        link.hidden = true;
      } else {
        const q = new URLSearchParams({ ruum: room, tyyp: this.type, p: String(l), l: String(w), k: String(h) });
        link.href = `${ROOT}pages/valguskalkulaator?${q.toString()}`;
      }
    }
  }

  async add(btn) {
    const quantity = this.qty || 1;
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Lisan…';
    const live = this.querySelector('[data-live]');
    try {
      await addToCart(this, [{ id: this.p.id, quantity }]);
      btn.textContent = 'Lisatud ostukorvi ✓';
      if (live) live.textContent = `${quantity} tk lisati ostukorvi.`;
    } catch (e) {
      btn.textContent = 'Ei õnnestunud – proovi uuesti';
    }
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = label;
    }, 2600);
  }
}


/* ---------- LED-riba ja toiteploki kalkulaatorid ---------- */

const PSU_COLS = ['toiteplokid-pistikuga', 'toiteplokid-hamardamatud'];
const psuCache = new Map();
function loadPsus(col) {
  if (!psuCache.has(col)) {
    psuCache.set(
      col,
      fetch(`${ROOT}collections/${col}?view=nl-psu`, { credentials: 'same-origin' })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then((t) => (JSON.parse(t).products || []).map((p) => ({ ...p, col })))
        .catch((e) => {
          psuCache.delete(col);
          throw e;
        })
    );
  }
  return psuCache.get(col);
}

/** Toiteploki väljundpinge numbrina (12, 24, 48). Vahemikuga (12–24 V) draiverid jäetakse välja. */
function psuVolt(v) {
  const t = String(v || '');
  if (/[–-]/.test(t)) return 0;
  const m = t.match(/(\d+)\s*V/);
  return m ? parseInt(m[1], 10) : 0;
}

/** Valib väikseima piisava toiteploki: kuni 72 W eelistab pistikuga mudelit (lihtne kodus kasutada). */
function pickPsu(psus, volt, needW) {
  const ok = psus.filter((p) => p.a && psuVolt(p.v) === volt && parseFloat(p.w) >= needW);
  if (!ok.length) return null;
  const sorter = (a, b) => parseFloat(a.w) - parseFloat(b.w) || a.p - b.p;
  const plug = ok.filter((p) => p.col === 'toiteplokid-pistikuga').sort(sorter);
  if (needW <= 72 && plug.length) return plug[0];
  return ok.sort(sorter)[0];
}

class NlCalcStrip extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._abort = new AbortController();
    const d = this.dataset;
    this.strip = { id: parseInt(d.variant, 10), p: parseInt(d.price, 10), wpm: parseFloat(d.wpm) || 0, lpm: parseFloat(d.lpm) || 0, roll: parseFloat(d.roll) || 5, volt: parseInt(d.volt, 10) || 0, mains: d.mains === 'true', a: d.available === 'true' };
    const input = this.querySelector('input[name="len"]');
    input.value = String(this.strip.roll);
    this.addEventListener('input', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.update(), 300);
    }, { signal: this._abort.signal });
    this.addEventListener('click', (e) => {
      const b = e.target instanceof Element ? e.target.closest('[data-add]') : null;
      if (b) this.add(b);
    }, { signal: this._abort.signal });
    this.psus = null;
    if (!this.strip.mains && this.strip.volt) {
      Promise.all(PSU_COLS.map((c) => loadPsus(c).catch(() => [])))
        .then((lists) => {
          this.psus = lists.flat();
          this.update();
        });
    }
    this.update();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._ready = false;
  }

  update() {
    const st = this.strip;
    const len = parseFloat(String(this.querySelector('input[name="len"]').value).replace(',', '.'));
    const out = this.querySelector('[data-out]');
    const actions = this.querySelector('[data-actions]');
    if (!(len > 0)) {
      out.textContent = 'Sisesta vajalik pikkus meetrites.';
      actions.innerHTML = '';
      return;
    }
    const meters = Math.max(1, Math.ceil(len - 0.001));
    const watts = st.wpm * len;
    const needW = watts * 1.2;
    let html = `<span class="nlc-pout__qty">Sul on vaja <strong>${meters} m</strong> riba</span>`;
    html += `<span class="nlc-pout__need">${fmtNum(len)} m × ${fmtNum(st.wpm)} W/m = <strong>${fmtNum(watts)} W</strong>${st.lpm ? ` · ${fmtInt(st.lpm * len)} lm` : ''}</span>`;
    html += `<span class="nlc-pout__need">Riba hind on ${money(st.p)} meetri kohta, ostukorvi läheb ${meters} m = <strong>${money(st.p * meters)}</strong>.</span>`;
    const items = [{ id: st.id, quantity: meters }];
    let total = st.p * meters;
    this.psu = null;
    if (st.mains) {
      html += `<span class="nlc-pnote nlc-pnote--ok">230 V riba ühendatakse otse vooluvõrku sobiva toitejuhtmega, eraldi toiteplokki pole vaja.</span>`;
    } else if (st.volt) {
      html += `<span class="nlc-pout__need">Toiteplokk: vähemalt <strong>${Math.ceil(needW)} W, ${st.volt} V</strong> (20% varuga)</span>`;
      if (this.psus) {
        const psu = pickPsu(this.psus, st.volt, needW);
        if (psu) {
          this.psu = psu;
          items.push({ id: psu.id, quantity: 1 });
          total += psu.p;
          html += `<a class="nlc-psu" href="${esc(psu.u)}">${psu.img ? `<img src="${esc(psu.img)}" alt="" width="48" height="48" loading="lazy">` : ''}<span><strong>Sobiv toiteplokk:</strong> ${esc(psu.t)}<br><em>${money(psu.p)}</em></span></a>`;
        } else {
          html += `<span class="nlc-pnote">Nii suure võimsuse jaoks jaga riba mitme toiteploki vahel või vaata <a href="${ROOT}collections/toiteplokid-din">DIN-toiteplokke</a>.</span>`;
        }
      }
      const maxRun = st.volt >= 24 ? 10 : 5;
      if (len > maxRun) html += `<span class="nlc-pnote nlc-pnote--ok">Üle ${maxRun} m pikkuse riba puhul ühenda toide mõlemast otsast, et heledus jääks ühtlaseks.</span>`;
    }
    out.innerHTML = html;
    this.items = items;
    const disabled = st.a ? '' : 'disabled';
    actions.innerHTML = this.psu
      ? `<button type="button" class="nlc-btn nlc-btn--primary" data-add="all" ${disabled}>Lisa korvi: ${meters} m riba + toiteplokk – ${money(total)}</button>
         <button type="button" class="nlc-btn nlc-btn--outline" data-add="strip" ${disabled}>Ainult riba (${meters} m)</button>`
      : `<button type="button" class="nlc-btn nlc-btn--primary" data-add="strip" ${disabled}>Lisa ${meters} m korvi – ${money(st.p * meters)}</button>`;
  }

  async add(btn) {
    const items = btn.dataset.add === 'all' ? this.items : this.items.slice(0, 1);
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Lisan…';
    const live = this.querySelector('[data-live]');
    try {
      await addToCart(this, items);
      btn.textContent = 'Lisatud ostukorvi ✓';
      if (live) live.textContent = 'Tooted lisati ostukorvi.';
    } catch (e) {
      btn.textContent = 'Ei õnnestunud – proovi uuesti';
    }
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = label;
    }, 2600);
  }
}

class NlCalcPsu extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this._abort = new AbortController();
    this.w = parseFloat(this.dataset.w) || 0;
    this.volt = parseInt(this.dataset.volt, 10) || 0;
    const usable = this.w / 1.2;
    const rows = this.volt === 48 ? [] : [4.8, 9.6, 14.4, 19.2];
    const tbody = this.querySelector('[data-rows]');
    if (tbody) {
      tbody.innerHTML = rows
        .map((wpm) => `<tr><td>${fmtNum(wpm)} W/m riba</td><td>kuni <strong>${fmtNum(Math.floor((usable / wpm) * 10) / 10)} m</strong></td></tr>`)
        .join('');
    }
    this.addEventListener('input', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.update(), 250);
    }, { signal: this._abort.signal });
    this.update();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._ready = false;
  }

  update() {
    const out = this.querySelector('[data-out]');
    const usable = this.w / 1.2;
    const wpmEl = this.querySelector('input[name="wpm"]');
    const lenEl = this.querySelector('input[name="len"]');
    const loadEl = this.querySelector('input[name="load"]');
    let load = 0;
    if (loadEl) load = parseFloat(String(loadEl.value).replace(',', '.')) || 0;
    else if (wpmEl && lenEl) load = (parseFloat(String(wpmEl.value).replace(',', '.')) || 0) * (parseFloat(String(lenEl.value).replace(',', '.')) || 0);
    if (!(load > 0)) {
      out.innerHTML = `<span class="nlc-pout__need">Selle toiteploki soovituslik koormus on kuni <strong>${fmtNum(Math.floor(usable * 10) / 10)} W</strong> (20% varuga).</span>`;
      return;
    }
    const need = load * 1.2;
    if (need <= this.w) {
      out.innerHTML = `<span class="nlc-pout__qty"><strong>Sobib ✓</strong> ${fmtNum(load)} W koormus jääb toiteploki piiresse.</span><span class="nlc-pout__need">Varu jääb ${Math.round((1 - load / this.w) * 100)}%.</span>`;
    } else if (load <= this.w) {
      out.innerHTML = `<span class="nlc-pout__qty"><strong>Napilt</strong> – ${fmtNum(load)} W mahub ära, aga varu jääb alla 20%.</span><span class="nlc-pout__need">Pikema eluea jaoks vali vähemalt ${Math.ceil(need)} W toiteplokk.</span>`;
    } else {
      out.innerHTML = `<span class="nlc-pout__qty"><strong>Liiga väike</strong> – vaja on vähemalt ${Math.ceil(need)} W toiteplokki.</span><span class="nlc-pout__need">Vaata suuremaid <a href="${ROOT}collections/toiteplokid-hamardamatud">${this.volt} V toiteplokke</a>.</span>`;
    }
  }
}

if (!customElements.get('nl-light-calc')) customElements.define('nl-light-calc', NlLightCalc);
if (!customElements.get('nl-calc-product')) customElements.define('nl-calc-product', NlCalcProduct);
if (!customElements.get('nl-calc-strip')) customElements.define('nl-calc-strip', NlCalcStrip);
if (!customElements.get('nl-calc-psu')) customElements.define('nl-calc-psu', NlCalcPsu);
